import { useStateMachine } from "#/components/context/stateMachine/StateMachineContext"
import { Steps } from "#/components/cmd-enrollment/workflow/steps"
import React, { useEffect } from "react"
import { BenefitEnrollmentFlowModalBackButton } from "../../shared"
import useSubmitWithErrorCatch from "#/components/cmd-enrollment/hooks/useSubmitWithErrorCatch"
import { Form, FormTextField } from "@carrotfertility/carotene-core-x"
import { Typography, Box, Button, ForwardIcon, Stack, useTheme } from "@carrotfertility/carotene-core"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { FormattedMessage, useIntl } from "react-intl"
import { useProgressTracker } from "../../../views/modal/ProgressTracker"

type PartnerNameFormData = {
  firstNameValue: string
  lastNameValue: string
}

const FieldNameFirstName = "firstNameValue"
const FieldNameLastName = "lastNameValue"

// Custom continue button that works with carotene-core-x Form
function ContinueButton(): JSX.Element {
  const intl = useIntl()
  const continueText = intl.formatMessage({ defaultMessage: "Continue" })
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
      <Button
        type="submit"
        size="medium"
        aria-label={continueText}
        sx={{ textWrap: "no-wrap" }}
        color="secondary"
        variant="outlined"
        endIcon={<ForwardIcon />}
      >
        {continueText}
      </Button>
    </Box>
  )
}

export default function PartnerFirstLastNameStep(): JSX.Element {
  const intl = useIntl()
  const theme = useTheme()
  const { updateUserInfo, partnerFirstName: defaultFirstName, partnerLastName: defaultLastName } = useCurrentUser()

  // Auto-skip logic: if both names exist, skip this step
  const shouldSkip = defaultFirstName && defaultLastName
  const { setNextStep } = useStateMachine(Steps.PARTNER_FIRST_LAST_NAME_CONNECT, shouldSkip ? "" : null)

  // Progress tracker
  const { showProgress } = useProgressTracker()
  useEffect(showProgress, [showProgress])

  // Form submission handler
  const handleFormSubmit = async (formData: PartnerNameFormData) => {
    const firstNameVal = formData[FieldNameFirstName]
    const lastNameVal = formData[FieldNameLastName]
    await updateUserInfo({
      partnerFirstName: firstNameVal,
      partnerLastName: lastNameVal
    })
    setNextStep(Steps.PARTNER_FIRST_LAST_NAME_CONNECT, "")
  }

  // Wrap with error catch to preserve error handling behavior
  const { onSubmit } = useSubmitWithErrorCatch(handleFormSubmit)

  return (
    <Form<PartnerNameFormData> onSubmit={onSubmit}>
      <Box>
        <BenefitEnrollmentFlowModalBackButton />
      </Box>
      <Typography
        id="step-heading"
        variant="h3"
        color={(theme) => theme.palette.text.primary}
        paddingBottom={(theme) => theme.tokens.spacing.sm}
      >
        <FormattedMessage defaultMessage="Your partner's name, as stated on their health insurance plan" />
      </Typography>
      <Box>
        <Stack spacing={theme.spacing(theme.tokens.spacing.md)}>
          <FormTextField
            name={FieldNameFirstName}
            label={intl.formatMessage({ defaultMessage: "First Name" })}
            defaultValue={defaultFirstName ?? ""}
            registerOptions={{
              required: intl.formatMessage({ defaultMessage: "Required" })
            }}
          />
          <FormTextField
            name={FieldNameLastName}
            label={intl.formatMessage({ defaultMessage: "Last Name" })}
            defaultValue={defaultLastName ?? ""}
            registerOptions={{
              required: intl.formatMessage({ defaultMessage: "Required" })
            }}
          />
        </Stack>
      </Box>
      <Box paddingTop={theme.spacing(theme.tokens.spacing.md)}>
        <ContinueButton />
      </Box>
    </Form>
  )
}
